import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AlertCircle, Check, Sparkles, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

/**
 * SoloRegister - Self-service registration for direct customers
 *
 * Key differences from partner/client registration:
 * - No invite code required
 * - 14-day free trial included
 * - Prepaid-only billing (no net-30)
 * - Simplified pricing displayed upfront
 */
export function SoloRegister() {
  const navigate = useNavigate();

  // Form state
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle Google OAuth signup
  const handleGoogleSignup = async () => {
    setError("");
    setIsGoogleLoading(true);

    try {
      const response = await fetch("/auth/google/signup", {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to initiate Google signup");
      }

      const { auth_url, state } = await response.json();

      // Store state in sessionStorage for CSRF validation
      sessionStorage.setItem("oauth_state", state);

      // Redirect to Google OAuth
      window.location.href = auth_url;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to start Google signup",
      );
      setIsGoogleLoading(false);
    }
  };

  // Calculate password strength
  const calculatePasswordStrength = (pwd: string): PasswordStrength => {
    let score = 0;

    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    if (score <= 2) return { score, label: "Weak", color: "bg-red-500" };
    if (score <= 3) return { score, label: "Medium", color: "bg-yellow-500" };
    if (score <= 4) return { score, label: "Strong", color: "bg-green-500" };
    return { score, label: "Very Strong", color: "bg-green-600" };
  };

  const passwordStrength = password
    ? calculatePasswordStrength(password)
    : null;

  // Validate password requirements
  const validatePassword = (): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/\d/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return "Password must contain at least one special character";
    }
    return null;
  };

  // Handle registration form submission
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate password
    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    // Check password match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    // Check terms agreement
    if (!agreedToTerms) {
      setError("You must agree to the Terms of Service");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/auth/register-solo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          full_name: fullName,
          password,
          company_name: companyName || null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Navigate to verification page
        navigate("/verify-email", {
          state: { email, justRegistered: true, isSolo: true },
        });
      } else {
        setError(data.detail || "Registration failed. Please try again.");
      }
    } catch (err) {
      setError("Registration failed. Please try again.");
      console.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#282828] via-[#4c1d95] to-[#282828] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 p-10 bg-[#282828]/80 backdrop-blur-sm rounded-2xl border border-[#1283EB]/30 shadow-2xl">
        {/* Logo/Header */}
        <div className="text-center">
          <img
            src="/aiia-logo-white.svg"
            alt="AIIA Logo"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-xl text-[#F8F3EC]">Start Your Free Trial</h1>
          <p className="mt-3 text-sm text-white/60">
            No credit card required. Get started in minutes.
          </p>
        </div>

        {/* Trial Benefits */}
        <div className="bg-gradient-to-r from-[#1283EB]/10 to-purple-500/10 border border-[#1283EB]/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5 text-[#1283EB]" aria-hidden="true" />
            <span className="font-medium text-white">
              14-Day Free Trial Includes:
            </span>
          </div>
          <ul className="space-y-2 text-sm text-white/70">
            <li className="flex items-center gap-2">
              <Check
                className="h-4 w-4 text-green-400 flex-shrink-0"
                aria-hidden="true"
              />
              <span>Full access to all BoBs</span>
            </li>
            <li className="flex items-center gap-2">
              <Check
                className="h-4 w-4 text-green-400 flex-shrink-0"
                aria-hidden="true"
              />
              <span>1 Odoo instance connection</span>
            </li>
            <li className="flex items-center gap-2">
              <Check
                className="h-4 w-4 text-green-400 flex-shrink-0"
                aria-hidden="true"
              />
              <span>Email support</span>
            </li>
          </ul>
        </div>

        {/* Google OAuth Signup */}
        <div>
          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={isGoogleLoading || loading}
            className="w-full bg-white hover:bg-gray-100 text-gray-900 font-medium py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGoogleLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span>Connecting to Google...</span>
              </>
            ) : (
              <>
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Sign up with Google</span>
              </>
            )}
          </button>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-[#282828]/80 text-white/50">
              Or create account with email
            </span>
          </div>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleRegisterSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-white/80 mb-1"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:border-transparent transition-all"
              placeholder="you@company.com"
              disabled={loading || isGoogleLoading}
            />
          </div>

          {/* Full Name */}
          <div>
            <label
              htmlFor="fullName"
              className="block text-sm font-medium text-white/80 mb-1"
            >
              Full Name
            </label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoComplete="name"
              className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:border-transparent transition-all"
              placeholder="John Smith"
              disabled={loading || isGoogleLoading}
            />
          </div>

          {/* Company Name (Optional) */}
          <div>
            <label
              htmlFor="companyName"
              className="block text-sm font-medium text-white/80 mb-1"
            >
              Company Name <span className="text-white/50">(Optional)</span>
            </label>
            <input
              id="companyName"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              autoComplete="organization"
              className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:border-transparent transition-all"
              placeholder="Your Company Inc."
              disabled={loading || isGoogleLoading}
            />
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-white/80 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:border-transparent transition-all pr-12"
                placeholder="Create a strong password"
                disabled={loading || isGoogleLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {password && passwordStrength && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <div
                    className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden"
                    role="progressbar"
                    aria-valuenow={passwordStrength.score}
                    aria-valuemin={0}
                    aria-valuemax={5}
                    aria-label={`Password strength: ${passwordStrength.label}`}
                  >
                    <div
                      className={`h-full ${passwordStrength.color} transition-all duration-300`}
                      style={{
                        width: `${(passwordStrength.score / 5) * 100}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs text-white/60 min-w-[80px]">
                    {passwordStrength.label}
                  </span>
                </div>
                <ul
                  className="text-xs text-white/50 space-y-1"
                  aria-label="Password requirements"
                >
                  <li className={password.length >= 8 ? "text-green-400" : ""}>
                    • At least 8 characters
                  </li>
                  <li
                    className={
                      /[A-Z]/.test(password) && /[a-z]/.test(password)
                        ? "text-green-400"
                        : ""
                    }
                  >
                    • Uppercase and lowercase letters
                  </li>
                  <li className={/\d/.test(password) ? "text-green-400" : ""}>
                    • At least one number
                  </li>
                  <li
                    className={
                      /[^A-Za-z0-9]/.test(password) ? "text-green-400" : ""
                    }
                  >
                    • At least one special character (!@#$%^&*)
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-white/80 mb-1"
            >
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:border-transparent transition-all pr-12"
                placeholder="Confirm your password"
                disabled={loading || isGoogleLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                aria-label={
                  showConfirmPassword
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
              >
                {showConfirmPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="mt-1 text-xs text-red-400" role="alert">
                Passwords do not match
              </p>
            )}
          </div>

          {/* Terms of Service */}
          <div className="flex items-start">
            <input
              id="terms"
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-white/10 bg-[#1a1a1a] text-[#1283EB] focus:ring-[#1283EB] focus:ring-offset-0"
            />
            <label htmlFor="terms" className="ml-2 text-sm text-white/70">
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1283EB] hover:text-[#1283EB]/80 underline"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1283EB] hover:text-[#1283EB]/80 underline"
              >
                Privacy Policy
              </a>
            </label>
          </div>

          {error && (
            <Alert
              variant="destructive"
              className="bg-red-900/20 border-red-800 text-red-400"
              role="alert"
            >
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <button
            type="submit"
            disabled={loading || isGoogleLoading || !agreedToTerms}
            className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#1283EB] hover:bg-[#1283EB]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1283EB] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating account..." : "Start Free Trial"}
          </button>
        </form>

        {/* Pricing Info */}
        <div className="text-center text-xs text-white/50">
          <p>After your trial, plans start at just $49/month</p>
        </div>

        {/* Footer */}
        <div className="text-center pt-4 border-t border-white/10">
          <p className="text-sm text-white/60">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-[#1283EB] hover:text-[#1283EB]/80 font-medium transition-colors"
            >
              Sign in →
            </Link>
          </p>
          <p className="text-sm text-white/60 mt-2">
            Have an invite code?{" "}
            <Link
              to="/register"
              className="text-[#1283EB] hover:text-[#1283EB]/80 font-medium transition-colors"
            >
              Register as Partner/Client →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SoloRegister;
